"use client";

/* eslint-disable react/jsx-no-constructed-context-values */
import { useTranslation } from "react-i18next";
import * as React from "react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import dynamic from "next/dynamic";
import dayjs, { Dayjs } from "dayjs";
import { usePathname, useRouter } from "next/navigation";
import Image from "next/image";

import { clampStyle } from "@audacia-hq/shared/utils";
import SharedComponents, { Alert, ConsentRenewalModal } from "@audacia-hq/shared/components";
import {
  Advisory, Announcement, Consent, SurveyType,
} from "@audacia-hq/shared/models";

import CreateMemberProfile, { CreateMemberProfileForm } from "../components/members/CreateMemberProfile";
import {
  Member, MemberForm, PendingActions,
} from "../models/member";
import Advisories from "../components/advisory/Advisories";
import { FetcherResult, useLazyFetcher } from "../hooks/useClientFetcher";
import { GET_CONTENT, GET_MEMBER, GET_PENDING_ACTIONS } from "../graphql/query";
import {
  ADD_MEMBER_FAVORITE, CHANGE_CONSENT, CHANGE_CONSENTS, CHANGE_WEEKLY_ORDERS_LIMIT, CREATE_MEMBER, REMOVE_MEMBER_FAVORITE, UPDATE_MEMBER,
} from "../graphql/mutations";
import { Content } from "../models/content";
import { Notification } from "../models/notification";
import { MemberQueueInfo } from "../models/consultation";
import { welcomeOfferEligibility } from "../services/api";
import sparkle from "../public/assets/login/sparkle.svg";

import { useAuth } from "./AuthContext";
import { useWS, WSMessage } from "./WSContext";
import { useTagManager } from "./TagManagerContext";

const TrialConsultationFeedbackForm = dynamic(() => import("../components/consultations/TrialConsultationFeedbackForm"));

interface MemberProviderProps {
  children: React.ReactNode;
  memberData?: Member;
  pendingActionsData?: PendingActions
  advisoriesData?: Advisory[];
  announcementsData?: Announcement[];
  notificationsData?: { notifications: Notification[], unreadNotificationsCount: number };
  surveysData?: SurveyType[];
  userQueuesData?: MemberQueueInfo[];
  err?: any;
}

interface MemberContextType {
  member?: Member;
  isEligibleForWelcomeOffer?: boolean;
  showWelcomeOfferModal: boolean;
  openWelcomeOfferModal: () => void;
  closeWelcomeOfferModal: (redeemed?: boolean) => void;
  pendingActions?: PendingActions;
  advisories?: Advisory[];
  announcements?: Announcement[];
  notifications?: { notifications: Notification[], unreadNotificationsCount: number };
  surveys?: SurveyType[];
  userQueues?: MemberQueueInfo[];
  handleUpdateMember: (m: Partial<MemberForm>) => Promise<FetcherResult<Member>>;
  refreshPendingActions: () => void;
  changeMemberConsent: (purposeUid: string, value: boolean) => void;
  changeMemberConsents: (consents: { purposeUid: string, value: boolean }[]) => Promise<void>;
  changeMemberOrdersLimit: (limit: number) => Promise<void>;
  changeQueueNotificationChannels: (push: boolean) => Promise<void>;
  addMemberFavorite: (favoriteId: string) => void;
  removeMemberFavorite: (favoriteId: string) => void;
  modalSignup: boolean;
  setModalSignup: (value: boolean) => void;
  handleMemberGet: () => void;
}

const MemberContext = React.createContext<MemberContextType>({
  showWelcomeOfferModal: false,
  openWelcomeOfferModal: () => undefined,
  closeWelcomeOfferModal: () => undefined,
  advisories: [],
  announcements: [],
  notifications: { notifications: [], unreadNotificationsCount: 0 },
  surveys: [],
  userQueues: [],
  handleUpdateMember: () => Promise.resolve({ data: null, error: null }),
  refreshPendingActions: () => ({}),
  changeMemberConsent: () => ({}),
  changeMemberConsents: () => Promise.resolve(),
  changeMemberOrdersLimit: () => Promise.resolve(),
  changeQueueNotificationChannels: () => Promise.resolve(),
  addMemberFavorite: () => null,
  removeMemberFavorite: () => null,
  handleMemberGet: () => null,
  modalSignup: false,
  setModalSignup: () => undefined,
});

const MemberProvider: React.FC<MemberProviderProps> = ({
  children,
  memberData,
  pendingActionsData,
  advisoriesData,
  announcementsData,
  notificationsData,
  surveysData,
  userQueuesData,
  err,
}) => {
  const { user, logout } = useAuth();

  const { sendInscriptionEvent } = useTagManager();
  const [showModal, setShowModal] = useState<string>();
  const [modalSignup, setModalSignup] = useState<boolean>(false);
  const [lastVisibility, setLastVisibility] = useState<Dayjs>();
  const [member, setMember] = useState<Member>(memberData?.profile ? memberData : undefined);
  const [isEligibleForWelcomeOffer, setIsEligibleForWelcomeOffer] = useState<boolean>(false);
  const [welcomeOfferOpen, setWelcomeOfferOpen] = useState<boolean>(false);

  const openWelcomeOfferModal = () => {
    setWelcomeOfferOpen(true);
  };

  const closeWelcomeOfferModal = (redeemed?: boolean) => {
    setWelcomeOfferOpen(false);
    if (redeemed) setIsEligibleForWelcomeOffer(false);
  };

  React.useEffect(() => {
    if (isEligibleForWelcomeOffer) return;
    if (!member || member.consultedExperts.length > 0 || member.profile.remainingTime > 0) return;
    welcomeOfferEligibility().then((r) => setIsEligibleForWelcomeOffer(r.data.promotionMutations.welcomeOfferEligibility));
  }, [member, isEligibleForWelcomeOffer]);

  const [error] = useState(err);
  const [pendingActions, setPendingActions] = useState<PendingActions>(pendingActionsData);
  const { t, i18n } = useTranslation(["member", "consultations", "register"]);
  const [getContent] = useLazyFetcher<{
    content: Content[]
  }>(GET_CONTENT, { authenticated: true });
  const ws = useWS();
  const router = useRouter();
  const pathname = usePathname();

  const [getPendingActions] = useLazyFetcher(GET_PENDING_ACTIONS, { authenticated: true });
  const [getMember] = useLazyFetcher(GET_MEMBER, { authenticated: true });
  const [updateMember] = useLazyFetcher(UPDATE_MEMBER, { authenticated: true });
  const [changeWeeklyOrdersLimit] = useLazyFetcher(CHANGE_WEEKLY_ORDERS_LIMIT, { authenticated: true });
  const [addFavorite] = useLazyFetcher(ADD_MEMBER_FAVORITE, { authenticated: true });
  const [removeFavorite] = useLazyFetcher(REMOVE_MEMBER_FAVORITE, { authenticated: true });
  const [changeConsent] = useLazyFetcher(CHANGE_CONSENT, { authenticated: true });
  const [changeConsents] = useLazyFetcher(CHANGE_CONSENTS, { authenticated: true });
  const [createMember] = useLazyFetcher(CREATE_MEMBER, { authenticated: true });

  const handlePendingActions = async () => {
    getPendingActions().then(({ data }) => setPendingActions(data?.pendingActions));
  };

  const handleMemberGet = async () => {
    const { data } = await getMember();
    setMember((data as any)?.member);
  };

  const handleUpdateMember = async (m: Partial<MemberForm>) => {
    const res = await updateMember({
      variables: {
        input: m,
      },
    });
    await handleMemberGet();
    return res;
  };

  const handleVisibilityChange = React.useCallback(() => {
    if (document.hidden) {
      setLastVisibility(dayjs());
      return;
    }
    if (!lastVisibility || dayjs().diff(lastVisibility, "second") < 30) return;
    if (!member) return;
    handlePendingActions();
  }, [lastVisibility, member]);

  useEffect(() => {
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [handleVisibilityChange]);

  useEffect(() => {
    const sub = ws.subscribe((msg) => handleMemberEvents(msg as WSMessage));
    return () => sub.unsubscribe();
  }, [ws]);

  useEffect(() => {
    if (error) {
      if ((error.messages?.includes("authentication.missing")
        || error.codes?.includes("AUTHENTICATION_ERROR")
        || error.codes?.includes("ForbiddenError")
        || error.codes?.includes("AuthenticationError")
      ) && user) {
        logout();
      }
      setShowModal("ERROR");
      return;
    }

    // (memberData?.memberUid === member?.memberUid) check is needed to avoid re-hydratation issues
    // SSR has new values after login => sets user of auth context FIRST, then memberData of this context,
    // causing !member?.profile && user to be true momentarily
    if ((memberData?.memberUid === member?.memberUid) && !member?.profile && user) {
      setShowModal("NO_MEMBER");
      return;
    }
    setShowModal(undefined);
  }, [error, member?.profile, user]);

  useEffect(() => {
    if (!user) {
      setMember(undefined);
    }
  }, [user]);

  const changeMemberConsents = async (consents: { purposeUid: string, value: boolean }[]) => {
    if (!member || !consents.length) return;
    const data = await changeConsents({
      variables: {
        input: consents,
      },
    }).then((d) => d.data);
    setMember((prevMember) => {
      const newConsents = (data as any).memberMutations.changeConsents;
      return {
        ...prevMember,
        consents: [
          ...prevMember.consents.filter((c) => !newConsents.some((nc) => nc.purposeUid === c.purposeUid)),
          ...newConsents,
        ],
      };
    });
  };

  const changeMemberConsent = (purposeUid: string, value: boolean) => {
    if (!member) return;
    changeConsent({
      variables: {
        input: { purposeUid, value },
      },
    }).then(({ data }) => {
      setMember((prevMember) => {
        const newConsent = data.memberMutations.changeConsent;
        return {
          ...prevMember,
          consents: [
            ...prevMember.consents.filter((c) => c.purposeUid !== newConsent.purposeUid),
            newConsent,
          ],
        };
      });
    });
  };

  const changeMemberOrdersLimit = (limit: number) => changeWeeklyOrdersLimit({
    variables: {
      weeklyOrdersLimit: limit,
    },
  }).then(async () => {
    setMember((prevMember) => ({
      ...prevMember,
      profile: {
        ...member.profile,
        weeklyOrdersLimit: limit,
      },
    }));
  });

  const addMemberFavorite = (favoriteId: string) => {
    if (!user) {
      setModalSignup(true);
      return;
    }
    addFavorite({ variables: { favorite: favoriteId } })
      .then(async () => {
        setMember((prevMember) => ({
          ...prevMember,
          profile: {
            ...member.profile,
            favorites: [...(member.profile.favorites || []), favoriteId],
          },
        }));
      })
      .then(() => toast.success(t("favorites.expertAdded")));
  };

  const removeMemberFavorite = (favoriteId: string) => {
    if (!user) {
      setModalSignup(true);
      return;
    }
    removeFavorite({ variables: { favorite: favoriteId } })
      .then(async () => {
        setMember((prevMember) => ({
          ...prevMember,
          profile: {
            ...member.profile,
            favorites: (prevMember.profile.favorites || []).filter((favorite) => favorite !== favoriteId),
          },
        }));
      })
      .then(() => toast.success(t("favorites.expertRemoved")));
  };

  const handleMemberEvents = (msg: WSMessage) => {
    switch (msg.event) {
      case "ws.reconnected":
        handleMemberGet();
        break;
      case "member.fidelity.changed":
        updateMemberFidelityPoints(JSON.parse(msg.payload).fidelityPoints);
        break;
      case "member.remainingTime.changed":
        updateRemainingTime(JSON.parse(msg.payload).remainingTime);
        break;
      case "logout":
        logout().then(() => router.push("/home"));
        break;
      default:
        break;
    }
  };

  const updateMemberFidelityPoints = (fidelityPoints: number) => {
    setMember((prevMember) => ({
      ...prevMember,
      profile: {
        ...prevMember.profile,
        fidelityPoints,
      },
    }));
  };

  const updateRemainingTime = (remainingTime: number) => {
    setMember((prevMember) => ({
      ...prevMember,
      profile: {
        ...prevMember.profile,
        remainingTime,
      },
    }));
  };

  const changeQueueNotificationChannels = async (push: boolean) => {
    const queueNotificationChannels = push ? ["PUSH"] : [];
    await updateMember({
      variables: {
        input: { queueNotificationChannels },
      },
    }).then(() => {
      setMember((prevMember) => ({
        ...prevMember,
        profile: {
          ...member.profile,
          queueNotificationChannels,
        },
      }));
    });
  };

  const saveMember = async ({
    usageConsent, newsletterConsent, exclusiveOffersConsent, ...m
  }: CreateMemberProfileForm) => {
    const res = await createMember({
      variables: {
        input: m,
        consents: {
          newsletterConsent,
          exclusiveOffersConsent,
        },
      },
    });
    if (res?.error?.messages?.includes("member.alreadyExist")) {
      toast.error(t("register:form.errors.invalidCredentials"));
      return;
    }
    await handleMemberGet();
    sendInscriptionEvent();
    setWelcomeOfferOpen(true);
  };

  // Modified consent purposes that have been accepted by the user, but now require further consent (e.g. updated terms of use, etc.)
  const [pendingConsents, setPendingConsents] = useState<(Consent & { content: string })[]>([]);
  useEffect(() => {
    // No race condition here please
    if (!member) {
      setPendingConsents([]);
      return () => { /* empty */ };
    }
    let active = true;
    fetchPendingConsents();

    return () => { active = false; };

    async function fetchPendingConsents() {
      const pendingConsents = member ? member.consents.filter((c) => c.requiresConsent && c.value && c.contentId && c.context === "idealvoyance") : [];

      // Get content for consents
      const contents = await getContent({
        variables: {
          locale: i18n.languages[0],
          namespace: "contents",
        },
      });

      if (!active) return;
      setPendingConsents(pendingConsents.map(
        (c) => ({
          ...c,
          content: contents.data?.content.find((content) => content.key === `terms.${c.contentId}`)?.content || "",
        }),
      ));
    }
  }, [
    member?.consents,
  ]);

  return (
    <MemberContext.Provider value={{
      member,
      isEligibleForWelcomeOffer,
      showWelcomeOfferModal: welcomeOfferOpen,
      openWelcomeOfferModal,
      closeWelcomeOfferModal,
      pendingActions,
      advisories: user ? advisoriesData : [],
      announcements: announcementsData,
      notifications: notificationsData,
      surveys: surveysData,
      userQueues: userQueuesData,
      modalSignup,
      handleUpdateMember,
      handleMemberGet,
      changeMemberConsent,
      changeMemberConsents,
      refreshPendingActions: handlePendingActions,
      changeMemberOrdersLimit,
      changeQueueNotificationChannels,
      addMemberFavorite,
      removeMemberFavorite,
      setModalSignup: (value) => (!user || !value) && setModalSignup(value),
    }}
    >
      {!showModal && advisoriesData?.length >= 1 && <Advisories initialAdvisories={advisoriesData} />}
      {!showModal && pendingConsents.length >= 1 && pathname !== "/support" && (
        <ConsentRenewalModal
          consents={pendingConsents}
          onAccept={(consents) => changeMemberConsents(consents.map((c) => ({ purposeUid: c.purposeUid, value: true })))}
          onSupport={() => router.push("/support")}
        />
      )}
      <SharedComponents.Modal show={showModal !== undefined && pathname !== "/terms-conditions/usage"} onClose={() => logout().then(() => setShowModal(undefined))}>
        {showModal === "ERROR" && (
          <div className="max-w-md m-auto p-6 space-y-6">
            <Alert type="alert">{t("common:errors.serviceUnavailable")}</Alert>
          </div>
        )}
        {user && showModal === "NO_MEMBER" && (
          <div className="flex flex-col pt-[6px] md:py-4 h-full sm:h-auto">
            <div className="flex flex-col gap-2 items-center w-full mb-4">
              <Image
                priority
                src={sparkle}
                alt="sparkle"
                style={{
                  ...clampStyle("width", [480, 768], [40, 72]),
                }}
              />
              <div className="text-2xl md:text-4xl font-semibold text-center">{t("onboarding.title2")}</div>
            </div>

            <CreateMemberProfile user={user} onCreateMember={saveMember} />
          </div>
        )}
      </SharedComponents.Modal>
      <SharedComponents.Modal title={t("consultations:trialConsultation.feedback.title")} show={pendingActions?.trialConsultation?.status === "PENDING_FEEDBACK"}>
        {pendingActions && <TrialConsultationFeedbackForm trialConsultation={pendingActions.trialConsultation} onSubmit={() => handlePendingActions()} />}
      </SharedComponents.Modal>
      {children}
    </MemberContext.Provider>
  );
};

const useMember = () => React.useContext(MemberContext);

export default MemberContext;
export { MemberProvider, useMember };
